import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { server } from "../../server";
import useAuth from "../../useAuth";
import {
  FaClock,
  FaWhatsapp,
  FaSms,
  FaEnvelope,
  FaPrint,
  FaPaperPlane,
  FaSearch,
  FaCheckSquare,
  FaSquare,
  FaTimes,
  FaTable,
  FaList,
  FaDownload,
  FaMoneyBillWave,
  FaGraduationCap,
  FaLayerGroup,
  FaShieldAlt,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { useReactToPrint } from "react-to-print";
import { useNavigate } from "react-router-dom";
import { classesList } from "../../jsonsFiles/classesList";

export default function UpcomingDueFeesPage({ isDefaultersOnly = false }) {
  const [viewMode, setViewMode] = useState("matrix");
  const [matrixData, setMatrixData] = useState([]);
  const [matrixMonths, setMatrixMonths] = useState([]);
  const [dueFees, setDueFees] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filters
  const [activeBucket, setActiveBucket] = useState(isDefaultersOnly ? "defaulters" : "today");
  const [selectedClass, setSelectedClass] = useState("ALL");
  const [selectedSection, setSelectedSection] = useState("ALL");
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [selectedStudentIds, setSelectedStudentIds] = useState([]);
  const { token } = useAuth();
  const navigate = useNavigate();
  const printMatrixRef = useRef();

  // Reminder Modal State
  const [isReminderModalOpen, setIsReminderModalOpen] = useState(false);
  const [reminderChannel, setReminderChannel] = useState("WhatsApp");
  const [customMessage, setCustomMessage] = useState("");

  const handlePrintMatrix = useReactToPrint({
    contentRef: printMatrixRef,
    documentTitle: `Fee_Dues_Matrix_${new Date().toISOString().split("T")[0]}`,
    pageStyle: `@page { size: A4 landscape; margin: 10mm; }`,
  });

  // Debounce search input (300ms)
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, 300);
    return () => clearTimeout(handler);
  }, [searchTerm]);

  // Fetch Matrix Data
  const fetchMatrixData = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (selectedClass !== "ALL") params.append("class", selectedClass);
      if (selectedSection !== "ALL") params.append("section", selectedSection);
      if (debouncedSearch) params.append("search", debouncedSearch);

      const res = await axios.get(`${server}/fee-due/matrix?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        setMatrixData(res.data.data || []);
        setMatrixMonths(res.data.months || []);
      }
    } catch (error) {
      toast.error("Failed to load fee dues matrix");
    } finally {
      setLoading(false);
    }
  };

  // Fetch Bucket Dues Data
  const fetchBucketFees = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append("bucket", activeBucket);
      if (selectedClass !== "ALL") params.append("class", selectedClass);
      if (selectedSection !== "ALL") params.append("section", selectedSection);
      if (debouncedSearch) params.append("search", debouncedSearch);

      const res = await axios.get(`${server}/fee-due/upcoming?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.data.success) {
        setDueFees(res.data.data || []);
      }
    } catch (error) {
      toast.error("Failed to load upcoming due fees");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      if (viewMode === "matrix") {
        fetchMatrixData();
      } else {
        fetchBucketFees();
      }
    }
  }, [token, viewMode, activeBucket, selectedClass, selectedSection, debouncedSearch]);

  // Export Matrix to CSV
  const exportMatrixCSV = () => {
    if (matrixData.length === 0) return toast.error("No data available to export");

    const headers = [
      "Student Name",
      "Admission No",
      "Roll No",
      "Class",
      "Section",
      "Guardian",
      "Phone",
      ...matrixMonths.map((m) => `${m} Due`),
      "Total Billed",
      "Total Paid",
      "Net Outstanding",
      "Status",
    ];

    const rows = matrixData.map((row) => [
      `"${row.studentName || ""}"`,
      `"${row.admissionNumber || ""}"`,
      `"${row.rollNumber || ""}"`,
      `"${row.class || ""}"`,
      `"${row.section || ""}"`,
      `"${row.guardian || ""}"`,
      `"${row.phone || ""}"`,
      ...matrixMonths.map((m) => row.months[m]?.due || 0),
      row.totalBilled || 0,
      row.totalPaid || 0,
      row.netBalance || 0,
      `"${row.status || ""}"`,
    ]);

    const csvContent =
      "data:text/csv;charset=utf-8," + [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `Fee_Dues_Matrix_${new Date().toISOString().split("T")[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const toggleSelectAll = () => {
    if (viewMode === "matrix") {
      if (selectedStudentIds.length === matrixData.length) {
        setSelectedStudentIds([]);
      } else {
        setSelectedStudentIds(matrixData.map((d) => d.studentId));
      }
    } else {
      if (selectedStudentIds.length === dueFees.length) {
        setSelectedStudentIds([]);
      } else {
        setSelectedStudentIds(dueFees.map((df) => df.studentId));
      }
    }
  };

  const toggleSelectStudent = (studentId) => {
    if (selectedStudentIds.includes(studentId)) {
      setSelectedStudentIds(selectedStudentIds.filter((id) => id !== studentId));
    } else {
      setSelectedStudentIds([...selectedStudentIds, studentId]);
    }
  };

  const handleSendRemindersSubmit = async (e) => {
    e.preventDefault();
    if (selectedStudentIds.length === 0) return toast.error("Please select at least one student!");

    try {
      const payload = {
        studentIds: selectedStudentIds,
        channel: reminderChannel,
        customMessage,
      };

      const res = await axios.post(`${server}/fee-due/send-reminders`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (res.data.success) {
        toast.success(res.data.message);
        setIsReminderModalOpen(false);
        setSelectedStudentIds([]);
      }
    } catch (error) {
      toast.error("Failed to send reminders");
    }
  };

  const totalOutstandingMatrix = matrixData.reduce((acc, r) => acc + (r.netBalance || 0), 0);
  const totalCollectedMatrix = matrixData.reduce((acc, r) => acc + (r.totalPaid || 0), 0);

  return (
    <div className="p-4 max-w-[1400px] mx-auto min-h-screen bg-gray-50 dark:bg-slate-950 text-gray-800 dark:text-slate-100 font-sans transition-colors duration-300">
      {/* Compact Top Header Bar */}
      <div className="bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-2xl px-5 py-3 mb-4 shadow-xs flex flex-col lg:flex-row justify-between items-start lg:items-center gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-100 dark:bg-amber-950 text-amber-600 dark:text-amber-400 rounded-xl shadow-xs">
            <FaShieldAlt size={18} />
          </div>
          <div>
            <h1 className="text-lg font-black text-gray-900 dark:text-white leading-tight">
              {isDefaultersOnly ? "Fee Defaulters Register" : "Pending & Upcoming Fee Dues Matrix"}
            </h1>
            <p className="text-xs text-gray-500 dark:text-slate-400 font-semibold">Month-by-month student fee dues grid & financial tracking</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto">
          {/* View Switcher Toggle */}
          <div className="bg-gray-100 dark:bg-slate-800 p-1 rounded-xl border border-gray-200 dark:border-slate-700 flex items-center">
            <button
              onClick={() => setViewMode("matrix")}
              className={`px-3 py-1 rounded-lg text-xs font-black transition flex items-center gap-1.5 ${
                viewMode === "matrix"
                  ? "bg-white text-gray-900 dark:bg-amber-500 dark:text-slate-950 shadow-xs"
                  : "text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white"
              }`}
            >
              <FaTable size={11} /> Matrix
            </button>
            <button
              onClick={() => setViewMode("buckets")}
              className={`px-3 py-1 rounded-lg text-xs font-black transition flex items-center gap-1.5 ${
                viewMode === "buckets"
                  ? "bg-white text-gray-900 dark:bg-amber-500 dark:text-slate-950 shadow-xs"
                  : "text-gray-500 dark:text-slate-400 hover:text-gray-900 dark:hover:text-white"
              }`}
            >
              <FaList size={11} /> Buckets
            </button>
          </div>

          {selectedStudentIds.length > 0 && (
            <button
              onClick={() => setIsReminderModalOpen(true)}
              className="bg-emerald-600 text-white px-3.5 py-1.5 rounded-xl flex items-center font-black text-xs hover:bg-emerald-500 transition shadow-sm"
            >
              <FaPaperPlane className="mr-1.5" /> Send Reminder ({selectedStudentIds.length})
            </button>
          )}
        </div>
      </div>

      {/* Compact Financial KPI Summary Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <div className="bg-white dark:bg-slate-900 p-3 rounded-xl border border-gray-200 dark:border-slate-800 text-center shadow-xs">
          <span className="text-[9px] font-extrabold uppercase tracking-wider text-gray-400 dark:text-slate-400">Total Listed Students</span>
          <div className="text-xl font-black text-gray-900 dark:text-white mt-0.5">{matrixData.length}</div>
        </div>
        <div className="bg-emerald-50 dark:bg-emerald-950/40 p-3 rounded-xl border border-emerald-200 dark:border-emerald-900 text-center shadow-xs">
          <span className="text-[9px] font-extrabold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">Total Billed Paid</span>
          <div className="text-xl font-black text-emerald-600 dark:text-emerald-400 mt-0.5">₹{totalCollectedMatrix.toLocaleString()}</div>
        </div>
        <div className="bg-rose-50 dark:bg-rose-950/40 p-3 rounded-xl border border-rose-200 dark:border-rose-900 text-center shadow-xs">
          <span className="text-[9px] font-extrabold uppercase tracking-wider text-rose-700 dark:text-rose-300">Total Net Outstanding</span>
          <div className="text-xl font-black text-rose-600 dark:text-rose-400 mt-0.5">₹{totalOutstandingMatrix.toLocaleString()}</div>
        </div>
        <div className="bg-amber-50 dark:bg-amber-950/40 p-3 rounded-xl border border-amber-200 dark:border-amber-900 text-center shadow-xs">
          <span className="text-[9px] font-extrabold uppercase tracking-wider text-amber-700 dark:text-amber-300">Academic Year</span>
          <div className="text-xl font-black text-amber-600 dark:text-amber-400 mt-0.5">2026-2027</div>
        </div>
      </div>

      {/* Compact Filter Toolbar */}
      <div className="bg-white dark:bg-slate-900 p-3.5 rounded-2xl border border-gray-200 dark:border-slate-800 shadow-xs mb-4">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-3">
            <select
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
              className="w-full px-3 py-1.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-xs font-bold text-gray-900 dark:text-slate-100 outline-none focus:border-amber-500"
            >
              <option value="ALL">All Classes</option>
              {classesList.map((c, idx) => (
                <option key={idx} value={c}>
                  Class - {c}
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-3">
            <select
              value={selectedSection}
              onChange={(e) => setSelectedSection(e.target.value)}
              className="w-full px-3 py-1.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-xs font-bold text-gray-900 dark:text-slate-100 outline-none focus:border-amber-500"
            >
              <option value="ALL">All Sections</option>
              {["A", "B", "C", "D", "E"].map((sec, idx) => (
                <option key={idx} value={sec}>
                  Section - {sec}
                </option>
              ))}
            </select>
          </div>

          <div className="md:col-span-6 relative">
            <FaSearch className="absolute left-3 top-2.5 text-gray-400 dark:text-slate-500 text-xs" />
            <input
              type="text"
              placeholder="Live search by Student Name, Reg No, Roll No..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg text-xs font-bold text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 outline-none focus:border-amber-500"
            />
          </div>
        </div>
      </div>

      {/* Due Buckets Selector Bar */}
      {viewMode === "buckets" && !isDefaultersOnly && (
        <div className="flex gap-1.5 overflow-x-auto pb-2 custom-scrollbar mb-4">
          {[
            { id: "today", label: "Due Today" },
            { id: "tomorrow", label: "Due Tomorrow" },
            { id: "3days", label: "Within 3 Days" },
            { id: "7days", label: "Within 7 Days" },
            { id: "14days", label: "Within 14 Days" },
            { id: "21days", label: "Within 21 Days" },
            { id: ">21days", label: "Above 21 Days" },
            { id: "defaulters", label: "Defaulters (Overdue)" },
          ].map((b) => (
            <button
              key={b.id}
              onClick={() => setActiveBucket(b.id)}
              className={`px-3 py-1 rounded-xl text-xs font-black transition whitespace-nowrap ${
                activeBucket === b.id
                  ? "bg-amber-500 text-slate-950 shadow-xs"
                  : "bg-white dark:bg-slate-900 text-gray-700 dark:text-slate-400 border border-gray-200 dark:border-slate-800 hover:bg-gray-100 dark:hover:bg-slate-800"
              }`}
            >
              {b.label}
            </button>
          ))}
        </div>
      )}

      {/* TABULAR FEE MATRIX VIEW */}
      {viewMode === "matrix" && (
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-gray-200 dark:border-slate-800 shadow-xs overflow-hidden mb-8">
          {/* Table Header Bar */}
          <div className="p-3 bg-gray-50 dark:bg-slate-800/60 border-b border-gray-200 dark:border-slate-800 flex justify-between items-center flex-wrap gap-2 text-xs">
            <button
              onClick={toggleSelectAll}
              className="flex items-center gap-1.5 font-black text-amber-600 dark:text-amber-400 hover:underline"
            >
              {selectedStudentIds.length === matrixData.length && matrixData.length > 0 ? (
                <FaCheckSquare size={14} />
              ) : (
                <FaSquare size={14} className="text-gray-300 dark:text-slate-600" />
              )}
              Select All ({matrixData.length} Students)
            </button>

            <div className="flex gap-2">
              <button
                onClick={exportMatrixCSV}
                className="px-3 py-1 bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-900 text-xs font-black rounded-lg hover:bg-emerald-100 transition flex items-center gap-1"
              >
                <FaDownload size={10} /> Export CSV
              </button>
              <button
                onClick={handlePrintMatrix}
                className="px-3 py-1 bg-indigo-50 text-indigo-700 dark:bg-indigo-950 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-900 text-xs font-black rounded-lg hover:bg-indigo-100 transition flex items-center gap-1"
              >
                <FaPrint size={10} /> Print Matrix
              </button>
            </div>
          </div>

          {/* Matrix Grid Table */}
          <div className="overflow-x-auto custom-scrollbar">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="bg-gray-50 dark:bg-slate-800/80 border-b border-gray-200 dark:border-slate-800 text-gray-500 dark:text-slate-400 uppercase font-black tracking-wider text-[10px]">
                  <th className="p-2 w-8 text-center">Select</th>
                  <th className="p-2 min-w-[150px]">Student Name</th>
                  <th className="p-2">Reg No</th>
                  <th className="p-2">Class (Sec)</th>
                  {matrixMonths.map((m, idx) => (
                    <th key={idx} className="p-2 text-center min-w-[65px]">
                      {m.substring(0, 3)}
                    </th>
                  ))}
                  <th className="p-2 text-right">Total Billed</th>
                  <th className="p-2 text-right">Total Paid</th>
                  <th className="p-2 text-right">Net Balance</th>
                  <th className="p-2 text-center">Status</th>
                  <th className="p-2 text-center">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-slate-800/80">
                {loading ? (
                  <tr>
                    <td colSpan={10 + matrixMonths.length} className="text-center py-12 text-amber-500 font-bold">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-amber-600 mx-auto mb-2"></div>
                      Loading student fee dues matrix...
                    </td>
                  </tr>
                ) : matrixData.length === 0 ? (
                  <tr>
                    <td colSpan={10 + matrixMonths.length} className="text-center py-12 text-gray-400 italic font-medium">
                      No student fee records found matching filter criteria.
                    </td>
                  </tr>
                ) : (
                  matrixData.map((row) => {
                    const isSelected = selectedStudentIds.includes(row.studentId);
                    return (
                      <tr
                        key={row.studentId}
                        className={`hover:bg-gray-50 dark:hover:bg-slate-800/50 transition-colors ${
                          isSelected ? "bg-amber-50 dark:bg-amber-500/10 border-l-2 border-l-amber-500" : ""
                        }`}
                      >
                        <td className="p-2 text-center">
                          <button onClick={() => toggleSelectStudent(row.studentId)}>
                            {isSelected ? (
                              <FaCheckSquare className="text-amber-500" />
                            ) : (
                              <FaSquare className="text-gray-300 dark:text-slate-700" />
                            )}
                          </button>
                        </td>
                        <td className="p-2 font-black text-gray-900 dark:text-white">
                          {row.studentName}
                          <span className="block text-[9px] font-medium text-gray-400 dark:text-slate-400">
                            {row.guardian} ({row.phone})
                          </span>
                        </td>
                        <td className="p-2 font-bold text-gray-600 dark:text-slate-300">{row.admissionNumber}</td>
                        <td className="p-2 font-bold text-gray-800 dark:text-slate-200">
                          Class {row.class} ({row.section})
                        </td>

                        {/* Month Cells Grid */}
                        {matrixMonths.map((m, idx) => {
                          const cell = row.months[m] || { status: "Unassigned", due: 0, paid: 0 };
                          let badgeClass = "bg-gray-100 text-gray-400 dark:bg-slate-800 dark:text-slate-500 border-gray-200 dark:border-slate-700/50";
                          let label = "-";

                          if (cell.status === "Paid") {
                            badgeClass = "bg-green-100 text-green-700 dark:bg-emerald-500/20 dark:text-emerald-400 border border-green-200 dark:border-emerald-500/30";
                            label = `₹${(cell.paid || 0).toLocaleString()}`;
                          } else if (cell.status === "Pending") {
                            badgeClass = "bg-red-100 text-red-700 dark:bg-rose-500/20 dark:text-rose-400 border border-red-200 dark:border-rose-500/30";
                            label = `₹${(cell.due || 0).toLocaleString()}`;
                          } else if (cell.status === "Partial") {
                            badgeClass = "bg-yellow-100 text-yellow-800 dark:bg-amber-500/20 dark:text-amber-400 border border-yellow-200 dark:border-amber-500/30";
                            label = `₹${(cell.due || 0).toLocaleString()}`;
                          }

                          return (
                            <td key={idx} className="p-1 text-center">
                              <span className={`px-1.5 py-0.5 rounded text-[9px] font-black inline-block w-full border ${badgeClass}`}>
                                {label}
                              </span>
                            </td>
                          );
                        })}

                        <td className="p-2 text-right font-black text-gray-900 dark:text-white">
                          ₹{(row.totalBilled || 0).toLocaleString()}
                        </td>
                        <td className="p-2 text-right font-black text-emerald-600 dark:text-emerald-400">
                          ₹{(row.totalPaid || 0).toLocaleString()}
                        </td>
                        <td className="p-2 text-right font-black text-rose-600 dark:text-rose-400">
                          ₹{(row.netBalance || 0).toLocaleString()}
                        </td>
                        <td className="p-2 text-center">
                          <span
                            className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase ${
                              row.status === "Paid"
                                ? "bg-green-100 text-green-700 dark:bg-emerald-500/20 dark:text-emerald-400"
                                : row.status === "Defaulter"
                                ? "bg-red-100 text-red-700 dark:bg-rose-500/20 dark:text-rose-400"
                                : "bg-orange-100 text-orange-700 dark:bg-amber-500/20 dark:text-amber-400"
                            }`}
                          >
                            {row.status}
                          </span>
                        </td>
                        <td className="p-2 text-center">
                          <button
                            onClick={() => navigate("/my/fees/collect")}
                            className="p-1.5 bg-emerald-50 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-500/30 hover:bg-emerald-100 rounded-lg font-bold transition"
                            title="Collect Fee"
                          >
                            <FaMoneyBillWave size={11} />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* DUE BUCKETS LIST VIEW */}
      {viewMode === "buckets" && (
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-gray-200 dark:border-slate-800 shadow-xs overflow-hidden mb-8">
          <div className="p-3 bg-gray-50 dark:bg-slate-800/60 border-b border-gray-200 dark:border-slate-800 flex justify-between items-center text-xs">
            <button
              onClick={toggleSelectAll}
              className="flex items-center gap-1.5 font-black text-amber-600 dark:text-amber-400 hover:underline"
            >
              {selectedStudentIds.length === dueFees.length && dueFees.length > 0 ? (
                <FaCheckSquare size={14} />
              ) : (
                <FaSquare size={14} className="text-gray-300 dark:text-slate-600" />
              )}
              Select All ({dueFees.length} Records)
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="bg-gray-50 dark:bg-slate-800/80 border-b border-gray-200 dark:border-slate-800 text-gray-500 dark:text-slate-400 uppercase font-black tracking-wider text-[10px]">
                  <th className="p-2.5 w-10 text-center">Select</th>
                  <th className="p-2.5">Student Name</th>
                  <th className="p-2.5">Reg No</th>
                  <th className="p-2.5">Class & Sec</th>
                  <th className="p-2.5">Guardian Contact</th>
                  <th className="p-2.5">Due Date</th>
                  <th className="p-2.5 text-right">Pending Amount</th>
                  <th className="p-2.5 text-center">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
                {loading ? (
                  <tr>
                    <td colSpan="8" className="text-center py-12 text-amber-500 font-bold">
                      Loading bucket due fees...
                    </td>
                  </tr>
                ) : dueFees.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center py-12 text-gray-400 italic">
                      No records found in this bucket.
                    </td>
                  </tr>
                ) : (
                  dueFees.map((df) => {
                    const isSelected = selectedStudentIds.includes(df.studentId);
                    return (
                      <tr
                        key={df.id}
                        className={`hover:bg-gray-50 dark:hover:bg-slate-800/50 transition ${
                          isSelected ? "bg-amber-50 dark:bg-amber-500/10" : ""
                        }`}
                      >
                        <td className="p-2.5 text-center">
                          <button onClick={() => toggleSelectStudent(df.studentId)}>
                            {isSelected ? (
                              <FaCheckSquare className="text-amber-500" />
                            ) : (
                              <FaSquare className="text-gray-300 dark:text-slate-700" />
                            )}
                          </button>
                        </td>
                        <td className="p-2.5 font-black text-gray-900 dark:text-white">{df.studentName}</td>
                        <td className="p-2.5 text-gray-500 dark:text-slate-400 font-bold">{df.admissionNumber}</td>
                        <td className="p-2.5 font-bold text-gray-800 dark:text-slate-200">
                          Class {df.class} ({df.section})
                        </td>
                        <td className="p-2.5 text-gray-500 dark:text-slate-400">
                          {df.guardian} ({df.phone})
                        </td>
                        <td className="p-2.5 font-bold text-gray-700 dark:text-slate-300">{df.dueDate}</td>
                        <td className="p-2.5 text-right font-black text-amber-600 dark:text-amber-400">
                          ₹{(df.pendingAmount || 0).toLocaleString()}
                        </td>
                        <td className="p-2.5 text-center">
                          <span
                            className={`px-2 py-0.5 rounded-full text-[9px] font-black uppercase ${
                              df.daysRemaining < 0
                                ? "bg-red-100 text-red-700 dark:bg-rose-500/20 dark:text-rose-400"
                                : "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400"
                            }`}
                          >
                            {df.dueStatus}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* REMINDER MODAL */}
      {isReminderModalOpen && (
        <div className="fixed inset-0 bg-slate-950/70 backdrop-blur-xs z-50 flex justify-center items-center p-4">
          <div className="bg-white dark:bg-slate-900 rounded-2xl p-6 max-w-md w-full shadow-2xl border border-gray-200 dark:border-slate-800 space-y-4">
            <div className="flex justify-between items-center border-b border-gray-100 dark:border-slate-800 pb-3">
              <h3 className="text-base font-black text-gray-900 dark:text-white flex items-center gap-2">
                <FaPaperPlane className="text-emerald-500" /> Dispatch Fee Dues Reminder
              </h3>
              <button onClick={() => setIsReminderModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleSendRemindersSubmit} className="space-y-3">
              <div>
                <label className="block text-[10px] font-extrabold uppercase tracking-wider text-gray-500 dark:text-slate-400 mb-1.5">
                  Select Channel
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { id: "WhatsApp", icon: FaWhatsapp, color: "text-emerald-500" },
                    { id: "SMS", icon: FaSms, color: "text-indigo-500" },
                    { id: "Email", icon: FaEnvelope, color: "text-rose-500" },
                  ].map((ch) => {
                    const Icon = ch.icon;
                    const isSelected = reminderChannel === ch.id;
                    return (
                      <button
                        type="button"
                        key={ch.id}
                        onClick={() => setReminderChannel(ch.id)}
                        className={`p-2.5 rounded-xl border text-xs font-black flex items-center justify-center gap-1.5 transition ${
                          isSelected
                            ? "bg-emerald-600 text-white border-emerald-600 shadow-xs"
                            : "bg-gray-50 dark:bg-slate-800 text-gray-700 dark:text-slate-300 border-gray-200 dark:border-slate-700 hover:bg-gray-100"
                        }`}
                      >
                        <Icon className={isSelected ? "text-white" : ch.color} size={12} /> {ch.id}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-extrabold uppercase tracking-wider text-gray-500 dark:text-slate-400 mb-1">
                  Custom Reminder Message
                </label>
                <textarea
                  rows="3"
                  value={customMessage}
                  onChange={(e) => setCustomMessage(e.target.value)}
                  placeholder="Dear Parent, Gentle reminder regarding pending fee dues for your ward..."
                  className="w-full p-2.5 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl text-xs text-gray-900 dark:text-white outline-none"
                ></textarea>
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t border-gray-100 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => setIsReminderModalOpen(false)}
                  className="px-4 py-2 rounded-xl text-xs font-bold text-gray-600 dark:text-slate-400 hover:bg-gray-100"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-emerald-600 text-white text-xs font-black rounded-xl hover:bg-emerald-500 transition shadow-md"
                >
                  Send Reminders Now
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Hidden Printable Fee Matrix Document */}
      <div className="hidden">
        <div ref={printMatrixRef} className="p-8 bg-white text-gray-900 font-sans w-[297mm] min-h-[210mm]">
          <div className="flex justify-between items-center border-b-2 border-amber-600 pb-4 mb-6">
            <div>
              <h1 className="text-2xl font-black uppercase text-gray-900">Dee Novo Public School</h1>
              <p className="text-xs text-gray-600 font-semibold">Comprehensive Student Monthly Fee Dues Matrix Report</p>
              <p className="text-xs text-gray-500">Date Generated: {new Date().toLocaleDateString()}</p>
            </div>
            <div className="text-right">
              <div className="text-xl font-black text-amber-600">MONTHLY FEE DUES MATRIX</div>
              <p className="text-xs text-gray-500">Academic Year 2026-2027</p>
            </div>
          </div>

          <table className="w-full text-left border-collapse text-[10px] mb-6">
            <thead>
              <tr className="bg-gray-900 text-white uppercase font-bold">
                <th className="p-2">Student Name</th>
                <th className="p-2">Admission No</th>
                <th className="p-2">Class (Sec)</th>
                {matrixMonths.map((m, idx) => (
                  <th key={idx} className="p-2 text-center">
                    {m.substring(0, 3)}
                  </th>
                ))}
                <th className="p-2 text-right">Billed</th>
                <th className="p-2 text-right">Paid</th>
                <th className="p-2 text-right">Outstanding</th>
                <th className="p-2 text-center">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-300">
              {matrixData.map((row, idx) => (
                <tr key={idx}>
                  <td className="p-2 font-bold">{row.studentName}</td>
                  <td className="p-2">{row.admissionNumber}</td>
                  <td className="p-2">
                    Class {row.class} ({row.section})
                  </td>
                  {matrixMonths.map((m, mIdx) => {
                    const cell = row.months[m] || { status: "Unassigned", due: 0, paid: 0 };
                    let label = cell.status === "Paid" ? `₹${cell.paid}` : cell.due > 0 ? `₹${cell.due}` : "-";
                    return (
                      <td key={mIdx} className="p-2 text-center font-bold">
                        {label}
                      </td>
                    );
                  })}
                  <td className="p-2 text-right font-bold">₹{(row.totalBilled || 0).toLocaleString()}</td>
                  <td className="p-2 text-right font-bold text-emerald-700">₹{(row.totalPaid || 0).toLocaleString()}</td>
                  <td className="p-2 text-right font-bold text-red-700">₹{(row.netBalance || 0).toLocaleString()}</td>
                  <td className="p-2 text-center font-bold">{row.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
